import React, { useCallback, useEffect, useState } from "react";
import { Box, Card, CardContent, CardHeader, TableFooter } from "@mui/material";
import { find, map, prop, propEq } from "ramda";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import { isBlank, notBlank } from "../../util/ext";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import makeStyles from "@mui/styles/makeStyles";
import {
  authTypes,
  useAdminAuthentication,
} from "../../manage-authentication/authenticationService";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import AuthenticationDetailsCell from "../../manage-authentication/AuthenticationDetailsCell";
import EditIcon from "@mui/icons-material/Edit";
import TablePagination from "@mui/material/TablePagination";
import AuthenticationListButton from "../../manage-authentication/AuthenticationListButton";
import { useAuthenticationsHandler } from "../../manage-authentication/use-AuthenticationsHandler";
import AuthenticationDialogs from "../../manage-authentication/AuthenticationDialogs";
import Button from "@mui/material/Button";
import { useNavigate, useLocation } from "react-router-dom";
import ConfirmButton from "../../components/ConfirmButton";

const useStyles = makeStyles(theme => ({
  header: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },

  content: {
    paddingTop: 0,
  },

  cell: {
    minWidth: 125,
  },
}));

const authDialogInitialState = {
  referer: false,
  ezproxy: false,
  shibboleth: false,
  ip: false,
  barcode: false,
  sip: false,
  papi: false,
  region: false,
  scope: false,
};

const OrganizationAuthenticationCard = ({ id }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const [authentications, setAuthentications] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);

  const authResponse = useAdminAuthentication(
    null,
    [],
    id,
    "dateUpdated:desc",
    page,
    4,
  );

  useEffect(() => {
    if (authResponse.isSuccess && notBlank(authResponse.data)) {
      setAuthentications(authResponse.data.contents);
      setTotal(authResponse.data.count);
    } else {
      setAuthentications([]);
      setTotal(0);
    }
  }, [authResponse.isSuccess, authResponse.data]);

  const updateResults = useCallback(
    async authentication => {
      await authResponse.refetch();
    },
    [authResponse],
  );

  const {
    authDialogs,
    currentAuthentication,
    handleAuthenticationDeleteClick,
    handleAuthenticationMenuSelect,
    handleAuthenticationEditClick,
    handleAuthenticationDialogClose,
    handleAuthenticationDialogSave,
  } = useAuthenticationsHandler({
    initialDialogState: authDialogInitialState,
    organizationId: id,
    callbacks: {
      afterDelete: updateResults,
      afterSave: updateResults,
    },
  });

  const handlePageChange = useCallback(page => setPage(page), []);

  return (
    <Card raised elevation={4} style={{ height: "100%" }}>
      <CardHeader
        titleTypographyProps={{
          variant: "h6",
          color: "primary",
        }}
        title="Authentications"
        action={
          <AuthenticationListButton
            title="Add Authentication"
            variant="icon"
            onAuthenticationSelected={handleAuthenticationMenuSelect}
          />
        }
        classes={{ root: classes.header }}
      />
      <CardContent classes={{ root: classes.content }}>
        {authResponse.isLoading && <PanelLoadingIndicator />}
        {authResponse.isError && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            Error Loading Authentications.
          </Box>
        )}
        {authResponse.isSuccess && isBlank(authentications) && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            No Authentications set up for this Organization.
          </Box>
        )}
        {authResponse.isSuccess && notBlank(authentications) && (
          <TableContainer>
            <Table stickyHeader aria-label="Authentications">
              <TableHead>
                <TableRow>
                  <TableCell key="type" align="left" className={classes.cell}>
                    Type
                  </TableCell>

                  <TableCell
                    key="date-updated"
                    align="left"
                    className={classes.cell}
                  >
                    Last Update
                  </TableCell>

                  <TableCell
                    key="details"
                    align="left"
                    // className={classes.cell}
                  >
                    Details
                  </TableCell>

                  <TableCell
                    key="actions"
                    align="center"
                    style={{ width: 100 }}
                  />
                </TableRow>
              </TableHead>

              <TableBody>
                {map(
                  auth => (
                    <TableRow hover tabIndex={-1} key={auth.authenticationId}>
                      <TableCell
                        key={"type:" + auth.authenticationId}
                        align="left"
                        className={classes.cell}
                      >
                        {prop(
                          "display",
                          find(propEq("value", auth.type), authTypes),
                        )}
                      </TableCell>

                      <TableCell
                        key={"date-updated:" + auth.authenticationId}
                        align="left"
                        className={classes.cell}
                      >
                        {new Date(auth.dateUpdated).toLocaleDateString()}
                      </TableCell>

                      <TableCell
                        key={"details:" + auth.authenticationId}
                        align="left"
                        className={classes.cell}
                      >
                        <AuthenticationDetailsCell authentication={auth} />
                      </TableCell>

                      <TableCell
                        key={"actions:" + auth.authenticationId}
                        align="center"
                        style={{ width: 100 }}
                      >
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          pl={4}
                        >
                          {(auth.type === "sip" ||
                            auth.type === "papi" ||
                            auth.type === "barcode") && (
                            <Tooltip title="Edit">
                              <IconButton
                                classes={{ label: classes.icon }}
                                onClick={() =>
                                  handleAuthenticationEditClick(auth)
                                }
                                size="large"
                              >
                                <EditIcon color="primary" fontSize="large" />
                              </IconButton>
                            </Tooltip>
                          )}

                          <ConfirmButton
                            handleDelete={() =>
                              handleAuthenticationDeleteClick(auth)
                            }
                            dialogTitle="Remove Authentication"
                            dialogBody="Are you sure you want to remove this authentication?"
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ),
                  authentications,
                )}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={2}>
                    <Button
                      variant="text"
                      onClick={() =>
                        navigate(`/manage-authentication`, {
                          state: {
                            organizationId: id,
                            previousLocation: location.pathname,
                          },
                        })
                      }
                    >
                      All Authentications
                    </Button>
                  </TableCell>

                  <TablePagination
                    classes={{
                      root: classes.pagination,
                    }}
                    rowsPerPageOptions={[4]}
                    colSpan={3}
                    count={total}
                    rowsPerPage={4}
                    page={page}
                    onPageChange={(event, page) => handlePageChange(page)}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        )}
      </CardContent>
      <AuthenticationDialogs
        organizationId={id}
        dialogsState={authDialogs}
        currentAuthentication={currentAuthentication}
        onClose={handleAuthenticationDialogClose}
        onSave={handleAuthenticationDialogSave}
      />
    </Card>
  );
};

export default OrganizationAuthenticationCard;
